import { memo, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { isNil, noop, isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import UI Components
import { BusyLoader, Modal, Button, Tooltip, Icon } from '@geneui/components';
// Import Components
import Typography from 'components/common/Typography';
import DownloadToolbar from 'components/common/DownloadToolbar';
import PlayersDataTable from 'components/common/PlayersDataTable';
import PromotionTrackedLinksContent from 'components/common/PromotionTrackedLinksContent';
// Import Constants
import { l, CampaignType, CommunicationTypeLabels } from 'constants/common';
import { PlayersColumns, ModalTypes, ModalTitleLabels } from './config';
// Import Services
import { CommunicationHttpService } from 'services/http';
// Import Helpers
import { saveToDisk } from 'services/Helpers';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Context
import { useCJWorkflowBlocks } from 'context/cjWorkflowBlocksContext';
// Import Styles
import 'assets/scss/customerJourneyReportPage.scss';
import playersDataTableStyles from 'components/common/PlayersDataTable/playersDataTable.module.scss';

const CJBlockInfoCell = ({ blockName }: { blockName: Nullable<string> }) => {
    const { t } = useTranslation();
    const { getBlockInfo } = useCJWorkflowBlocks();
    const blockInfo = blockName ? getBlockInfo(blockName) : null;

    if (blockInfo?.isRemoved) {
        return (
            <div className="live-monitoring-t-row live-monitoring-block-info">
                <p>{t(l.BlockRemoved)}</p>
                <Icon type="bc-icon-disable" className="live-monitoring-block-removed-icon" />
            </div>
        );
    }

    const typeText = blockInfo?.typeLabelKey ? ` | ${t(blockInfo.typeLabelKey)}` : '';
    const blockText = `${blockInfo ? blockInfo.displayName : t(l.NA)}${typeText}`;

    return (
        <div className="live-monitoring-t-row live-monitoring-block-info">
            <Typography text={blockText} className="live-monitoring-block-info-text" withEllipsis={true} />
        </div>
    );
};

const CustomerJourneyProgressData = ({ reportData, isLoadingReport, workflowId, promotionType }) => {
    const { t } = useTranslation();
    const { doPostRequest } = useRequest();
    const { ensureLoaded: ensureBlocksLoaded } = useCJWorkflowBlocks();

    useEffect(() => {
        ensureBlocksLoaded();
    }, [ensureBlocksLoaded]);

    const [selectedModalType, setSelectedModalType] = useState<string>();
    const [selectedTrackedLinkIds, setSelectedTrackedLinkIds] = useState([]);
    const [playersData, setPlayersData] = useState<Nullable<{ Data: AnyObject[] }>>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isLoadedModal, setIsLoadedModal] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState<Nullable<number>>(null);
    const [isFileLoading, setIsFileLoading] = useState(false);
    const [isDownloadDisabled, setIsDownloadDisabled] = useState(true);
    const isVisibleDownload = useMemo(
        () => selectedModalType === ModalTypes.Click || selectedModalType === ModalTypes.Open,
        [selectedModalType],
    );

    const linksTableTitle = useMemo(() => {
        switch (selectedModalType) {
            case ModalTypes.TrackedLink:
                return l.TrackedLink;
            case ModalTypes.UnsubscribeLink:
                return l.UnsubscribeLink;
            default:
                return '';
        }
    }, [selectedModalType]);

    const onCloseHandler = () => {
        setIsVisible(false);
    };

    const getFooter = () => {
        return (
            <Button appearance="minimal" onClick={onCloseHandler}>
                {t(l.Close)}
            </Button>
        );
    };

    const getContent = () => {
        if (selectedModalType === ModalTypes.TrackedLink) {
            return (
                <PromotionTrackedLinksContent
                    promotionId={workflowId}
                    templateId={selectedTemplateId}
                    campaignType={promotionType}
                    showOnlyUnsubscribeLinks={selectedModalType === ModalTypes.UnsubscribeLink}
                    templateName={reportData?.find((t) => t?.TemplateId === selectedTemplateId)?.TemplateName}
                    linksTableTitle={linksTableTitle}
                    setSelectedTrackedLinkIds={setSelectedTrackedLinkIds}
                    className={'c-j-report-page-promotion-tracked-links-wrapper'}
                />
            );
        }
        return (
            <div className="c-j-report-players-table">
                <div className="c-j-report-page-table">
                    <PlayersDataTable
                        config={PlayersColumns}
                        isLoading={isLoadedModal}
                        data={playersData}
                        TitleActions={
                            <>
                                {isVisibleDownload && (
                                    <div className={playersDataTableStyles.downloadPlayersToolbar}>
                                        <DownloadToolbar
                                            downloadClickHandler={downloadClickHandler}
                                            withCountDropdown={true}
                                            withTypeSelector={false}
                                            isFileLoading={isFileLoading}
                                            isDisableDownload={isDownloadDisabled}
                                        />
                                        <Tooltip text={t(l.TooltipDownloadTypeInfo)} position="top">
                                            <Icon type="bc-icon-info" />
                                        </Tooltip>
                                    </div>
                                )}
                            </>
                        }
                    />
                </div>
            </div>
        );
    };

    const {
        getWorkflowClickReportByCampaignType,
        getWorkflowOpenReportByCampaignTypeRequest,
        getUnsubscribeClickReportByCampaignTypeRequest,
        getUnsubscribeReportByCampaignTypeRequest,
    } = useCancelableRequests({
        getWorkflowClickReportByCampaignType: CommunicationHttpService.getClickReportByCampaignType,
        getUnsubscribeClickReportByCampaignTypeRequest:
            CommunicationHttpService.getUnsubscribeClickReportByCampaignType,
        getUnsubscribeReportByCampaignTypeRequest: CommunicationHttpService.getUnsubscribeReportByCampaignType,
        getWorkflowOpenReportByCampaignTypeRequest: CommunicationHttpService.getOpenReportByCampaignType,
    });

    // kept out of the hook: a file download must not be aborted when the user leaves the page
    const { downloadClickReportRequest, downloadOpenReportRequest } = useMemo(
        () => ({
            // eslint-disable-next-line no-restricted-syntax -- download must survive unmount
            downloadClickReportRequest: CommunicationHttpService.downloadClickReport(),
            // eslint-disable-next-line no-restricted-syntax -- download must survive unmount
            downloadOpenReportRequest: CommunicationHttpService.downloadOpenReport(),
        }),
        [],
    );

    const downloadClickHandler = (type, downloadDropdownValue) => {
        setIsFileLoading(true);

        const templateName = reportData?.find((t) => t?.TemplateId === selectedTemplateId)?.TemplateName;

        const requestBody = {
            TrackLinkIdes: selectedTrackedLinkIds,
            TopCount: downloadDropdownValue,
            CampaignType: CampaignType.WorkFlow,
            PromotionId: workflowId,
            TemplateId: selectedTemplateId,
        };

        switch (selectedModalType) {
            case ModalTypes.Click:
                return doPostRequest(downloadClickReportRequest.request, {
                    requestBody,
                }).then((res) => {
                    // Aborted on unmount: nothing to save
                    if (res.IsCanceled) return;
                    saveToDisk(res, type, `${templateName}_${ModalTypes.Click}`);
                    setIsFileLoading(false);
                });
            case ModalTypes.Open:
                return doPostRequest(downloadOpenReportRequest.request, {
                    requestBody,
                }).then((res) => {
                    // Aborted on unmount: nothing to save
                    if (res.IsCanceled) return;
                    saveToDisk(res, type, `${templateName}_${ModalTypes.Open}`);
                    setIsFileLoading(false);
                });
            default:
                return noop;
        }
    };

    const initModal = (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        request: Nullable<(...args: any[]) => Promise<any>> = null,
        templateId: Nullable<number>,
        campaignType = CampaignType.WorkFlow,
    ) => {
        if (!isNil(request)) {
            setPlayersData(null);
            setIsLoadedModal(true);
            setIsVisible(true);
            setSelectedTemplateId(templateId);

            doPostRequest(request, {
                requestBody: {
                    PromotionId: workflowId,
                    CampaignType: campaignType,
                    TemplateId: templateId,
                },
                successCallback: (Data) => {
                    setPlayersData({ Data });
                    setIsDownloadDisabled(isEmpty(Data));
                },
            }).then(() => {
                setIsLoadedModal(false);
            });
        }
    };

    const openCountClickHandler = (templateId) => {
        setSelectedModalType(ModalTypes.Open);
        initModal(getWorkflowOpenReportByCampaignTypeRequest.request, templateId, promotionType);
    };

    const clickCountClickHandler = (templateId) => {
        setSelectedModalType(ModalTypes.Click);
        initModal(getWorkflowClickReportByCampaignType.request, templateId, promotionType);
    };

    const unsubscribeClickCountClickHandler = (templateId) => {
        setSelectedModalType(ModalTypes.UnsubscribeLinkClick);
        initModal(getUnsubscribeClickReportByCampaignTypeRequest.request, templateId);
    };

    const unsubscribeCountClickHandler = (templateId) => {
        setSelectedModalType(ModalTypes.UnsubscribeLink);
        initModal(getUnsubscribeReportByCampaignTypeRequest.request, templateId);
    };

    const clickCountLinkClickHandler = (templateId, modalType) => {
        setSelectedModalType(modalType);
        setIsVisible(true);
        setSelectedTemplateId(templateId);
    };

    const renderCount = (value: Nullable<number>, onClick: () => void) =>
        (value ?? 0) > 0 ? (
            <button type="button" onClick={onClick}>
                {value}
            </button>
        ) : (
            <p>{value ?? t(l.NA)}</p>
        );

    return (
        <>
            <Modal
                title={t(l[ModalTitleLabels[selectedModalType ?? '']])}
                visible={isVisible}
                withPortal={true}
                closable={false}
                className="clicked-tracked-modal c-tracked-modal-wrapper"
                footer={getFooter()}
                onCancel={onCloseHandler}
            >
                {getContent()}
            </Modal>
            <div className="live-monitoring-table-wrp">
                <div className="live-monitoring-table-header">
                    <div>{t(l.BlockInfo)}</div>
                    <div>{t(l.Channel)}</div>
                    <div>{t(l.TemplateName)}</div>
                    <div>{t(l.SuccessSentCount)}</div>
                    <div>{t(l.TrackedLink)}</div>
                    <div>{t(l.Open)}</div>
                    <div>{t(l.Click)}</div>
                    <div>{t(l.ClickOnUnsubscribeLink)}</div>
                </div>

                <div className="live-monitoring-table-content">
                    <BusyLoader isBusy={isLoadingReport}>
                        {!isNil(reportData) &&
                            reportData.map((i) => (
                                <div
                                    className="live-monitoring-table-content-inner"
                                    key={`${i.BlockName}_${i.TemplateId}`}
                                >
                                    <CJBlockInfoCell blockName={i.BlockName} />
                                    <div className="live-monitoring-t-row">
                                        <p>
                                            {CommunicationTypeLabels[i.Channel]
                                                ? t(l[CommunicationTypeLabels[i.Channel]])
                                                : t(l.NA)}
                                        </p>
                                    </div>
                                    <div className="live-monitoring-t-row">
                                        <p>{i.TemplateName}</p>
                                    </div>
                                    <div className="live-monitoring-t-row">
                                        <p>{i.Sent ?? 0}</p>
                                    </div>
                                    <div className="live-monitoring-t-row">
                                        {i.TrackedLinkCount > 0 ? (
                                            <button
                                                onClick={() =>
                                                    clickCountLinkClickHandler(i.TemplateId, ModalTypes.TrackedLink)
                                                }
                                            >
                                                {i.TrackedLinkCount}
                                            </button>
                                        ) : (
                                            <p>{i.TrackedLinkCount ?? t(l.NA)}</p>
                                        )}
                                    </div>
                                    <div className="live-monitoring-t-row">
                                        {i.TotalOpened > 0 ? (
                                            <button onClick={() => openCountClickHandler(i.TemplateId)}>
                                                {i.TotalOpened}
                                            </button>
                                        ) : (
                                            <p>{i.TotalOpened ?? t(l.NA)}</p>
                                        )}
                                    </div>
                                    <div className="live-monitoring-t-row">
                                        {i.TotalClicked > 0 ? (
                                            <button onClick={() => clickCountClickHandler(i.TemplateId)}>
                                                {i.TotalClicked}
                                            </button>
                                        ) : (
                                            <p>{i.TotalClicked ?? t(l.NA)}</p>
                                        )}
                                    </div>
                                    <div className="live-monitoring-t-row live-monitoring-t-row--split">
                                        {renderCount(i.TotalClickUnsubscribed, () =>
                                            unsubscribeClickCountClickHandler(i.TemplateId),
                                        )}
                                        {i.TotalUnsubscribed > 0 && (
                                            <>
                                                <span className="live-monitoring-t-separator">/</span>
                                                {renderCount(i.TotalUnsubscribed, () =>
                                                    unsubscribeCountClickHandler(i.TemplateId),
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                    </BusyLoader>
                </div>
            </div>
        </>
    );
};

CustomerJourneyProgressData.propTypes = {
    workflowId: PropTypes.string,
    promotionType: PropTypes.number,
    isLoadingReport: PropTypes.bool,
    reportData: PropTypes.array,
};

CustomerJourneyProgressData.defaultProps = {
    reportData: [],
};

export default memo(CustomerJourneyProgressData);
